import _ from 'lodash';

import {
  API,
  API_FLAG,
  USER_ROLES,
  BUILDEE_SERVICES,
  ONE_MASTER_FLAG,
  ENTER_WORKER_FIELD_TREE_ID_PARAM_NAME,
} from './constants';
import {
  stringToNumberDefault,
  isNullOrEmpty,
  isNullOrUndefinedOrEmptyStr,
  replaceAll,
  iosCheck,
} from './stringUtil';
import SessionStorageService from '../services/utilities/SessionStorageService';
import parts from '../utilities/parts';
import LocalStorageService from '../services/utilities/LocalStorageService';
import { isTargetRoleId } from '../utilities/roleUtility';
import RoanUtility from './roanUtility';
import { goChangedFieldPage } from './fieldUtil';
import { hasOnlyRouanPermission } from '../utilities/companyUserPermissionUtil';
import AuthProvider, { needTokenUpdate } from '../utilities/authProvider';

/**
 * フラグ判定
 *  isApiFlagOff
 *  isApiFlagOn
 *    1 or 0 の場合に True False を返す
 */
export const isApiFlagOff = value => {
  const flag = _.isString(value) ? stringToNumberDefault(value) : value;
  if (!_.isNumber(flag)) {
    return false;
  }
  return flag === API_FLAG.Off;
};

export const isApiFlagOn = value => {
  const flag = _.isString(value) ? stringToNumberDefault(value) : value;
  if (!_.isNumber(flag)) {
    return false;
  }
  return flag === API_FLAG.On;
};

/**
 * @description 変換前の値とチェック値が一致した場合、デフォルト値に変換
 * @param {*} value 変換前の値
 * @param {array} checkValues チェック値
 * @param {*} defaultValue デフォルト値
 * @return {*} 一致: デフォルト値, 不一致: 変換前の値
 */
export const matchDefaultTo = (value, checkValues, defaultValue) => {
  if (!_.isArray(checkValues)) {
    throw new Error('checkValues not array.');
  }

  const isConversion = _.some(checkValues, checkValue => checkValue === value);
  return isConversion ? defaultValue : value;
};

/**
 * 端末判定
 *  isIPad
 *    iPadなら True を返す
 */
export const isIPad = () => {
  return (
    navigator.userAgent.indexOf('iPad') > -1 ||
    document.ontouchstart !== undefined
  );
};

/**
 * @description blob形式をjson形式に変換
 * @param {*} blob blob形式
 * @return {*} json形式 ※json形式への変換が失敗した場合は、blob形式を返却
 */
export const blobToJsonConversion = async blob => {
  const fileReader = new FileReader();

  return new Promise((resolve, reject) => {
    fileReader.onerror = () => {
      fileReader.abort();
      reject();
    };

    fileReader.onload = () => {
      let response;
      try {
        response = JSON.parse(fileReader.result);
      } catch (e) {
        response = fileReader.result;
      }
      resolve(response);
    };
    fileReader.readAsText(blob);
  });
};

/**
 * @description CSVダウンロード
 * @param {string} apiPath APIパス
 * @param {object} params 絞り込みパラメータ
 * @param {string} method GET or POST
 */
export const csvDownloader = async (apiPath, params = {}, method = 'GET') => {
  let token = SessionStorageService.getToken();
  if (needTokenUpdate(token)) {
    token = await AuthProvider.instance.silentTokenUdate();
  }
  params.csv_token = token;
  apiPath = `${API.endpoint}/${apiPath}`;

  if (method === 'GET') {
    let queryParams = '?';
    _.each(params, (value, key) => {
      if (!isNullOrEmpty(value)) {
        if (!_.isArray(value)) {
          queryParams += `${key}=${encodeURIComponent(value)}&`;
        } else {
          _.each(value, item => {
            queryParams += `${key}[]=${encodeURIComponent(item)}&`;
          });
        }
      }
    });
    queryParams = queryParams.slice(0, -1);

    window.location.href = `${apiPath}${queryParams}`;
  } else {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = apiPath;
    form.enctype = 'application/x-www-form-urlencoded';
    let paramElement;

    _.each(params, (value, key) => {
      if (!isNullOrEmpty(value)) {
        paramElement = document.createElement('input');
        if (!_.isArray(value)) {
          paramElement.type = 'hidden';
          paramElement.name = key;
          paramElement.value = value;
          form.appendChild(paramElement);
        } else {
          _.each(value, item => {
            paramElement.type = 'hidden';
            paramElement.name = `${key}[]`;
            paramElement.value = item;
            form.appendChild(paramElement);
            paramElement = document.createElement('input');
          });
        }
      }
    });

    document.body.appendChild(form);
    form.submit();
  }
};

/**
 * @description タイマー
 * @param {number} millisecond: ミリ秒
 * @return Promise
 */
export const setWaitTime = millisecond => {
  return new Promise(resolve => {
    const waitTimer = setTimeout(function() {
      resolve();
      clearTimeout(waitTimer);
    }, millisecond);
  });
};

/**
 * @description APIレスポンスからエラーメッセージを抽出する
 * @param {object} response: APIレスポンス
 * @return {string} message: エラーメッセージ
 */
export const extractErrorMessage = response => {
  if (isNullOrUndefinedOrEmptyStr(response.message) === true) {
    if (
      isNullOrUndefinedOrEmptyStr(response.data) === false &&
      typeof response.data.message === 'string'
    ) {
      return response.data.message;
    } else if (
      isNullOrUndefinedOrEmptyStr(response.data.error) === false &&
      typeof response.data.error === 'string'
    ) {
      return response.data.error;
    }
  }

  return response.message;
};

/**
 * プロパティの存在確認とプロパティ個数の確定、hasOwnプロパティ限定
 * @param {*} object 対象オブジェクト
 * @param {*} propertyPaths プロパティ名をカンマ区切りで指定した文字列
 */
export const checkProperty = (object, propertyPaths) => {
  if (!parts.inProperty(object, propertyPaths)) {
    return false;
  }
  if (parts.propertyCount(object) !== propertyPaths.split(',').length) {
    return false;
  }
  return true;
};

export const fixProperty = (object, propertyPaths) => {
  const paths = replaceAll(propertyPaths, ' ', '').split(',');
  const result = paths.every(path => _.has(object, path));
  if (!result) {
    return false;
  }
  return _.size(object) === paths.length;
};

/**
 * @description 非同期リクエストを連続実行を防止して呼び出す。API固有の処理はないがAPI呼び出し専用で使う。
 * @param {Function} asyncF: 非同期メソッド
 * @return {Function} 連続実行を防止した非同期メソッド
 * @example
 * submit = onceAsyncRequest(async () => {
 *  await request(); //必ず非同期メソッドはawaitすること
 * });
 */
export const onceAsyncRequest = asyncF => {
  let isWaiting = false;

  return async function(...args) {
    // 待機中なので何もしない
    if (isWaiting) {
      return;
    }

    isWaiting = true;
    const result = await asyncF.apply(this, args);
    isWaiting = false;
    return result;
  };
};

/**
 * 労務安全システムから、調整会議システムが呼び出された場合の
 * 戻り先のURLを作成する処理
 * @param {*} domainUrl
 * @param {*} queryParameter
 * @param {Array.<string>} deleteParams 除外パラメータリスト
 */
export const getGreenFileSystemUrl = (
  domainUrl,
  queryParameter,
  deleteParams = []
) => {
  if (queryParameter === '') {
    return;
  }
  // '?'がない場合や、'?'以降がない場合は queryParam は空文字になるので
  // 処理を行わない

  // クエリパラメータをオブジェクトにセット
  const formatQueryParams = {};
  _.each(queryParameter.split('&'), param => {
    const [key, value] = param.split('=');
    if (!_.isUndefined(key) && !_.isUndefined(value)) {
      formatQueryParams[key] = value;
    } else if (!_.isUndefined(key)) {
      formatQueryParams[key] = undefined;
    }
  });

  if (
    !_.has(formatQueryParams, '__sender') ||
    !_.has(formatQueryParams, '__callback')
  ) {
    return;
  }
  // __sender や __callback がない場合は処理を行わない

  if (_.has(formatQueryParams, 'roanToChoseiFlg')) {
    delete formatQueryParams.roanToChoseiFlg;
    SessionStorageService.setRoanToChoseiFlg(true);
  }
  // クエリパラメータ roanToChoseiFlg がある場合、削除しセッション情報に格納する

  if (formatQueryParams.__sender !== 'greenfile') {
    return;
  }
  // __sender が greenfile ではない場合は処理を行わない

  if (_.isUndefined(formatQueryParams.__callback)) {
    return;
  }
  // __callback が存在しない場合は処理を行わない
  // ※  __callback=&param1=abc なら空文字として存在するが
  //     __callback&param1=abc なら存在しないとみなされる

  delete formatQueryParams.__sender;
  const action = formatQueryParams.__callback;
  delete formatQueryParams.__callback;

  _.each(deleteParams, deleteParam => {
    delete formatQueryParams[deleteParam];
  });

  let greenFileSystemUrl = `${domainUrl}?__sender=conference&__action=${action}`;
  _.each(formatQueryParams, (value, key) => {
    if (!_.isUndefined(value)) {
      greenFileSystemUrl += `&${key}=${value}`;
    } else {
      greenFileSystemUrl += `&${key}`;
    }
  });

  if (!_.has(formatQueryParams, '__currentFieldId')) {
    greenFileSystemUrl = setFieldIdToURL(new URL(greenFileSystemUrl)).toString();
  }

  return greenFileSystemUrl;
};

/**
 * YYYY/MM/DD 形式のデータかどうかを確認する
 */
export const matchFormatDateSlash = value => {
  if (!_.isString(value)) {
    return false;
  }
  return value.match(/^\d{1,4}\/\d{1,2}\/\d{1,2}$/) ? true : false;
};

/**
 * YYYY-MM-DD 形式のデータかどうかを確認する
 */
export const matchFormatDateHyphen = value => {
  if (!_.isString(value)) {
    return false;
  }
  return value.match(/^\d{1,4}-\d{1,2}-\d{1,2}$/) ? true : false;
};

/**
 * 999-9999 形式のデータかどうかを確認する
 */
export const matchFormatPostalCodeHyphen = value => {
  if (!_.isString(value)) {
    return false;
  }
  return value.match(/^\d{3}-\d{4}$/) ? true : false;
};

/**
 * 会社IDを取得する
 * 協力会社管理者は複数の会社に所属し得るため、SessionStorageに格納された元請け選択画面で選択した会社IDを利用する必要がある
 * それ以外のロールの場合はLoacalStorageに格納された会社IDを取得する
 * */
export const getCompanyId = () => {
  let companyId = 0;
  if (isTargetRoleId([USER_ROLES.partner], LocalStorageService.getRoleId())) {
    // 協力会社管理者の場合はsessionから取得
    companyId = SessionStorageService.getSessionPartnerCompanyIdOfPrimeCompany();
  } else {
    companyId = SessionStorageService.getCompanyId();
  }
  return companyId;
};

export const checkCallBackGreenFileService = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const url = SessionStorageService.getItem('url');
  return Number(_.defaultTo(params.__switch_user, 0)) === 1 && url;
};

export const getQueryStringParameter = queryKey => {
  const urlSearchParams = window.URLSearchParams;
  const queryString = window.location.search;

  if (urlSearchParams) {
    const searchParams = new urlSearchParams(queryString.replace('?', ''));
    return searchParams.get(queryKey) || null;
  } else {
    // for IE11
    const regex = new RegExp(`[?&]${queryKey}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(queryString);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return null;
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }
};

export const getOneMasterTypeNameCompanyNumber = (
  oneMasterFlag,
  companyNumber
) => {
  let oneMasterTypeNameCompanyNumber = '';
  Object.keys(ONE_MASTER_FLAG).forEach(function(key) {
    if (ONE_MASTER_FLAG[key].id === oneMasterFlag) {
      oneMasterTypeNameCompanyNumber = ONE_MASTER_FLAG[key].name;
    }
  });
  if (companyNumber) {
    oneMasterTypeNameCompanyNumber = `${oneMasterTypeNameCompanyNumber}（法人番号：${companyNumber}）`;
  }
  return oneMasterTypeNameCompanyNumber;
};

/**
 * 直リンクのアクションに遷移する
 */
export const directLinkAction = async directLink => {
  SessionStorageService.removeItem('relayPoint');
  SessionStorageService.removeItem('directLink');
  SessionStorageService.removeItem('afterLoginEventsFlag');
  let actionPath = directLink.action_path;
  if (directLink.service_id === BUILDEE_SERVICES.ROAN.ID) {
    RoanUtility.setSessionSelectedPartnerCompanyIdBeforeMoveRoan();
    const url = new URL(process.env.REACT_APP_GREENFILE_URL + _.toString(actionPath))
    if (directLink.field_id) {
      url.searchParams.set('__currentFieldId', directLink.field_id);
    }
    window.location.replace(url);
    return;
  }
  if (!actionPath) {
    if (SessionStorageService.getItem('field_id')) {
      goChangedFieldPage(directLink.service_id);
      return;
    } else {
      actionPath = '/conference/Fields/0/0/0/0';
    }
  }
  if (directLink.service_id === BUILDEE_SERVICES.COMMON.ID) {
    // 共通への遷移で労安権限のみしか保持していない場合は「閉じる」ボタン押下時に労安画面へ遷移するように url を設定する
    const companyId = getCompanyId();
    const result = await hasOnlyRouanPermission(companyId);
    if (result) {
      const url = SessionStorageService.getItem('url');
      if (url === null) {
        const domainGreenFile = process.env.REACT_APP_GREENFILE_URL;
        const greenFileSystemUrl = getGreenFileSystemUrl(
          domainGreenFile,
          '__sender=greenfile&__callback=GRN_001'
        );
        SessionStorageService.setItem('url', greenFileSystemUrl);
      }
    }
  }
  if (
    [BUILDEE_SERVICES.CONFERENCE.ID, BUILDEE_SERVICES.COMMON.ID].includes(
      directLink.service_id
    )
  ) {
    // 現場選択済みの場合は、ダッシュボードを踏ませる
    const path = actionPath.split('/');
    if (path.length >= 7 && path[1] === BUILDEE_SERVICES.CONFERENCE.KEYWORD) {
      const dashboard0 = 'Dashboard:0';
      if (SessionStorageService.getItem('field_id')) {
        if (path[5] === '0') {
          path[5] = 'Dashboard:1';
        }
        if (path[6].indexOf('?') === -1) {
          if (path[6] !== dashboard0) {
            path[6] = dashboard0;
          }
        } else {
          if (path[6].substring(0, path[6].indexOf('?')) !== dashboard0) {
            path[6] = dashboard0 + path[6].substring(path[6].indexOf('?'));
          }
        }
      }
      actionPath = path.join('/');
    }
  }
  window.history.pushState(null, null, actionPath);
  window.location.reload();
};

/**
 * enterWorkerFieldTreeIdパラメーターがある場合は、労安の作業員名簿編集画面に遷移する
 */
export const redirectToGreenfileSagyouinEditIfFromGreenfile = () => {
  const enterWorkerFieldTreeId = getQueryStringParameter(
    ENTER_WORKER_FIELD_TREE_ID_PARAM_NAME
  );
  if (enterWorkerFieldTreeId) {
    const greenFileSystemUrl = getGreenFileSystemUrl(
      process.env.REACT_APP_GREENFILE_URL,
      location.search.substring(1),
      [ENTER_WORKER_FIELD_TREE_ID_PARAM_NAME]
    );
    if (greenFileSystemUrl) {
      RoanUtility.setSessionSelectedPartnerCompanyIdBeforeMoveRoan();
      window.location.replace(greenFileSystemUrl);
      return;
    }
  }
};

export const calcOptionData = ({
  start_value,
  total_option,
  counter_value,
  unit_name,
  isDesc = false,
}) => {
  const data = [];
  for (
    let i = start_value;
    i < start_value + counter_value * total_option;
    i += counter_value
  ) {
    data.push({ value: i, name: i + unit_name });
  }
  if (isDesc) {
    return data.reverse();
  }
  return data;
};

export const getZendeskUrl = (path, param) => {
  const URIValue = encodeURIComponent(param).replace(/%20/g, '+');
  let zendeskUrl = '';
  if (_.isEmpty(param)) {
    zendeskUrl = `${path}hc/ja/`;
  } else {
    zendeskUrl = `${path}hc/ja/search?utf8=✓&query=${URIValue}`;
  }
  return zendeskUrl;
};

export const getZendeskArticleUrl = (path, articleId) => {
  let zendeskArticleUrl = '';
  let id = _.toString(articleId);
  if (_.isEmpty(id)) {
    zendeskArticleUrl = `${path}hc/ja/`;
  } else {
    zendeskArticleUrl = `${path}hc/ja/articles/${id}`;
  }
  return zendeskArticleUrl;
};

export const getTransitionEndEventName = () => {
  const element = document.createElement('div');

  const transEndEventNames = {
    WebkitTransition: 'webkitTransitionEnd',
    MozTransition: 'transitionend',
    OTransition: 'oTransitionEnd otransitionend',
    transition: 'transitionend',
  };

  for (const name in transEndEventNames) {
    if (element.style[name] !== undefined) {
      return transEndEventNames[name];
    }
  }

  return false;
};

/**
 * check IOS Safari version
 * @param {number} stopVersion: default 13
 * @return {boolean} true: current version is equal specified stop version or lower
 */
export const isSafariStopVersion = (stopVersion = 13) => {
  if (iosCheck()) {
    const ua = navigator.userAgent.toLowerCase();
    const matches = ua.match(/version\/(\d+)/);
    if (matches) {
      const osVersion = _.toNumber(matches[1]);
      return osVersion <= stopVersion;
    }
  }
  return false;
};

// Safariブラウザーを使用しているかの確認 // BLDE_V2-45041
export const isSafariBrowser = () => {
  const userAgentString = navigator.userAgent;
  const chromeAgent = userAgentString.indexOf('Chrome') > -1;
  const safariAgent = userAgentString.indexOf('Safari') > -1;
  if (chromeAgent) {
    return false;
  } else if (safariAgent) {
    return true;
  }
  return false;
};

/**
 * 引数がすべて配列かどうかを確認する
 * Check if all items in arguments are arrays
 * @param  {...any} args
 * @returns {boolean} true: All items in args are arrays
 */
export const isArrayAll = (...args) => _.every(args, _.isArray);

/**
 * 引数がすべて関数かどうかを確認する
 * Check if all items in arguments are functions
 * @param  {...any} args - Arguments to check
 * @returns {boolean} true: All items in args are functions
 */
export const isFunctionAll = (...args) => _.every(args, _.isFunction);

/**
 * 引数がすべてundefinedではないことを確認する
 * Checks if none of the arguments are undefined
 * @param  {...any} args
 * @returns {boolean} true: すべてundefinedではない
 */
export const isNotUndefinedAll = (...args) =>
  _.every(args, arg => !_.isUndefined(arg));

/**
 * 全選択フラグ確認
 * @param {array}selecteds: 選択要素
 * @param {array}options: 選択項目リスト
 */
export const checkSelectedAll = (selecteds, options) =>
  options.length > 0 && _.every(options, option => _.includes(selecteds, option));

/**
 * URLにクエリパラメタ__currentFieldIdを付与する
 * @param {URL} url
 * @returns {URL}
 */
export const setFieldIdToURL = (url) => {
  url.searchParams.set('__currentFieldId', sessionStorage.getItem('field_id'));
  return url;
}



// WEBPACK FOOTER //
// ./src/utilities/commonUtil.js